import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'react-bootstrap/Button';
import { Dropdown, DropdownBody } from '../../components/dropdown';
import { ReactComponent as DotsIconSvg } from '../../shared/icons/dotsRoundIcon.svg';

const DotsIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><DotsIconSvg /></span>
));
import RenameFolderModal from './components/RenameFolderModal';
import MoveFolderModal from './components/MoveFolderModal';
import DeleteFolderModal from './components/DeleteFolderModal';
import { useDropdownControl } from '../../contexts/DropdownContext';
import styles from './styles.module.scss';

type FolderControlsProps = {
  folderId: string;
  folderName: string;
  editPermission: boolean;
  managePermission?: boolean; // New prop for manage_templates permission
  teamId: string;
  onSuccess: () => void;
};

type DropdownActionsType = {
  text: string;
  onClick: () => void;
  isAvailable: boolean;
};

enum FolderActionStatus {
  rename = 'Rename',
  move = 'Move',
  delete = 'Delete',
}

const FolderControls = ({
  folderId,
  folderName,
  editPermission,
  managePermission,
  teamId,
  onSuccess,
}: FolderControlsProps) => {
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dropdownId = useMemo(() => `folder-dropdown-${folderId}`, [folderId]);
  const { isOpen, onToggle } = useDropdownControl(dropdownId);
  const isManageAvailable = managePermission ?? editPermission; // Use managePermission if provided, fallback for backward compatibility

  const allDropDownActions = [
    {
      text: FolderActionStatus.rename,
      onClick: () => {
        setShowRenameModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: FolderActionStatus.move,
      onClick: () => {
        setShowMoveModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: FolderActionStatus.delete,
      onClick: () => {
        setShowDeleteModal(true);
      },
      isAvailable: isManageAvailable,
    },
  ];

  const dropDownActions = (actions: DropdownActionsType[]) => {
    return actions
      .filter((action) => action.isAvailable)
      .map((action, index) => (
        <Dropdown.Item as={Button} variant="link" onClick={action.onClick} key={index}>
          {action.text}
        </Dropdown.Item>
      ));
  };

  // Only show dropdown if user has permission for any actions
  const availableActions = allDropDownActions.filter((action) => action.isAvailable);
  const hasAvailableActions = availableActions.length > 0;

  return (
    <>
      {hasAvailableActions && (
        <Dropdown show={isOpen} onToggle={onToggle}>
          <Dropdown.Toggle id="dropdown-folder-actions" className={styles.dottedDropdown} as={DotsIcon}>
            <FormattedMessage id="folder.actions" defaultMessage={`Folder Actions`} />
          </Dropdown.Toggle>
          <Dropdown.Menu align="right">
            <DropdownBody>{dropDownActions(allDropDownActions)}</DropdownBody>
          </Dropdown.Menu>
        </Dropdown>
      )}

      <RenameFolderModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        folderId={folderId}
        currentName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
        folderType="template"
      />

      <MoveFolderModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        folderId={folderId}
        folderName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
        folderType="template"
      />

      <DeleteFolderModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        folderId={folderId}
        folderName={folderName}
        teamId={teamId}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default FolderControls;
